import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, Globe, School, X, Mic, Users, MessageSquare } from 'lucide-react';
import { db, auth } from '../firebase';
import * as Tone from 'tone';
import { getDoc, doc, collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { useIdentity } from '../context/IdentityContext';

const modeSelectionCache = {
  userCollege: 'Unknown',
  isInitialized: false,
};

export const ModeSelectionPage = () => {
  const { identityMode } = useIdentity();
  const navigate = useNavigate();
  const location = useLocation();
  const featureType = location.state?.featureType || 'chat'; // 'chat', 'voice1v1', 'voice4v4'
  
  const [selectedLocation, setSelectedLocation] = useState('campus');
  const [userCollege, setUserCollege] = useState(modeSelectionCache.userCollege);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(!modeSelectionCache.isInitialized);

  useEffect(() => {
    const fetchUserCollege = async () => {
      const user = auth.currentUser;
      if (!user) return navigate('/login');
      
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const college = userDoc.data()?.college || 'Unknown';
        setUserCollege(college);
        modeSelectionCache.userCollege = college;
        modeSelectionCache.isInitialized = true;
      } catch (e) {
        // console.error("College fetch error", e);
      } finally {
        setIsInitializing(false);
      }
    };
    if (!modeSelectionCache.isInitialized) {
      fetchUserCollege();
    }
  }, [navigate]);

  const handleSlayPress = async () => {
    if (isLoading || isInitializing) return;
    setIsLoading(true);

    try {
      if (featureType !== 'chat') {
        try {
          await Tone.start();
        } catch (e) {
          console.error("Tone.start error:", e);
        }
      }

      if (featureType === 'chat') {
        const user = auth.currentUser;
        if (user) {
          const queueRef = collection(db, 'anonymousChatQueueV2');
          const existingQ = query(queueRef, where('uid', '==', user.uid));
          const existingSnap = await getDocs(existingQ);

          if (existingSnap.empty) {
            await addDoc(queueRef, {
              uid: user.uid,
              mode: selectedLocation,
              college: userCollege,
              timestamp: serverTimestamp(),
            });
          }
        }
        navigate('/chat/searching', { state: { selectedMode: selectedLocation, userCollege } });
      } else if (featureType === 'voice1v1') {
        navigate('/find-voice-match-search', { state: { selectedMode: selectedLocation, userCollege } });
      } else if (featureType === 'voice4v4') {
        navigate('/group-voice-setup', { state: { selectedMode: selectedLocation, userCollege } });
      }
    } catch(e) {
      console.error("Slay press error:", e);
    } finally {
      setIsLoading(false);
    }
  };

  if (isInitializing) {
    return (
        <div className="w-full sm:w-[700px] min-h-screen bg-black flex items-center justify-center border-r border-gray-800 shrink-0">
            <div className="w-8 h-8 rounded-full border-t-2 border-b-2 border-[#0BD9E5] animate-spin"></div>
        </div>
     );
  }

  const getFeatureConfig = () => {
    switch(featureType) {
        case 'chat': return { icon: MessageSquare, label: '1-1 Chat', color: '#0BD9E5' };
        case 'voice1v1': return { icon: Mic, label: '1-1 Room', color: '#BD00FF' };
        case 'voice4v4': return { icon: Users, label: '4-4 Room', color: '#FF6600' };
        default: return { icon: Zap, label: 'Connect', color: '#ffffff' };
    }
  };

  const config = getFeatureConfig();
  const FeatureIcon = config.icon;

  if (identityMode === 'real') {
     return (
       <main className="w-full sm:w-[700px] min-h-screen pb-28 sm:pb-6 border-r border-gray-800 flex flex-col items-center justify-center p-8 shrink-0 relative bg-[#0a0a0a]">
          <div className="w-16 h-16 rounded-full bg-[#0BD9E5]/20 text-[#0BD9E5] flex items-center justify-center mb-6">
             <FeatureIcon size={32} />
          </div>
          <h2 className="text-[31px] font-extrabold mb-2 leading-tight text-center text-white">{config.label}</h2>
          <p className="text-gray-500 text-[15px] text-center">Coming soon for Real Identity mode.</p>
       </main>
     );
  }

  return (
    <main className="w-full sm:w-[700px] min-h-screen pb-36 sm:pb-6 border-r border-gray-800 flex flex-col shrink-0 relative overflow-hidden bg-black text-white font-poppins selection:bg-[#0BD9E5]/30">
      
      <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(11,217,229,0.1),transparent_70%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px]" />
      </div>

      <div className="relative z-10 flex flex-col h-full p-6 max-w-md mx-auto w-full">
          <div className="flex items-center justify-between mb-8 mt-4">
              <div className="w-10"></div>
              <div className="px-4 py-1.5 rounded-full bg-[#111] border border-gray-800 flex items-center gap-2">
                 <FeatureIcon size={14} style={{ color: config.color }} />
                 <span className="text-xs font-bold tracking-widest uppercase">{config.label}</span>
              </div>
              <div className="w-10"></div>
          </div>

          <div className="flex-1 flex flex-col items-center justify-center -mt-10 w-full">
              <h2 className="text-4xl font-black mb-10 tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400">
                  Select Mode
              </h2>

              <div className="grid grid-cols-2 md:flex-row gap-3 md:gap-5 w-full">
                  <button 
                      onClick={() => setSelectedLocation('campus')}
                      className={`relative flex-1 p-4 sm:p-8 rounded-2xl sm:rounded-3xl border text-left transition-all overflow-hidden ${selectedLocation === 'campus' ? 'border-[#0BD9E5] bg-[#0BD9E5]/10 scale-[1.02]' : 'border-gray-800 bg-[#111] hover:border-gray-600 hover:bg-[#1a1a1a]'}`}
                  >
                      <School className={`w-6 h-6 sm:w-8 sm:h-8 mb-2 sm:mb-4 ${selectedLocation === 'campus' ? 'text-[#0BD9E5]' : 'text-gray-500'}`} />
                      <h3 className={`text-lg sm:text-2xl font-bold mb-1 sm:mb-2 ${selectedLocation === 'campus' ? 'text-white' : 'text-gray-300'}`}>Campus</h3>
                      <p className={`text-xs sm:text-sm ${selectedLocation === 'campus' ? 'text-gray-300' : 'text-gray-500'}`}>
                          Talk with verified students from <span className="text-[#0BD9E5] font-semibold block sm:inline">{userCollege === 'Unknown' ? 'your college' : userCollege}</span>
                      </p>
                  </button>

                  <button 
                      onClick={() => setSelectedLocation('global')}
                      className={`relative flex-1 p-4 sm:p-8 rounded-2xl sm:rounded-3xl border text-left transition-all overflow-hidden ${selectedLocation === 'global' ? 'border-[#BD00FF] bg-[#BD00FF]/10 scale-[1.02]' : 'border-gray-800 bg-[#111] hover:border-gray-600 hover:bg-[#1a1a1a]'}`}
                  >
                      <Globe className={`w-6 h-6 sm:w-8 sm:h-8 mb-2 sm:mb-4 ${selectedLocation === 'global' ? 'text-[#BD00FF]' : 'text-gray-500'}`} />
                      <h3 className={`text-lg sm:text-2xl font-bold mb-1 sm:mb-2 ${selectedLocation === 'global' ? 'text-white' : 'text-gray-300'}`}>Global</h3>
                      <p className={`text-xs sm:text-sm ${selectedLocation === 'global' ? 'text-gray-300' : 'text-gray-500'}`}>
                          Connect with students across the world <br className="hidden sm:block"/>
                          <span className="text-[#BD00FF] font-semibold text-[10px] sm:text-[11px] uppercase tracking-wider block mt-1 sm:mt-2">
                              {featureType === 'chat' ? '(All Colleges Worldwide)' : '(Colleges Worldwide)'}
                          </span>
                      </p>
                  </button>
              </div>
          </div>

          <div className="mt-auto pt-8 pb-24 sm:pb-6 flex justify-center w-full">
              <button 
                  onClick={handleSlayPress}
                  disabled={isLoading || (selectedLocation === 'campus' && userCollege === 'Unknown')}
                  className="w-full h-16 bg-white text-black font-bold uppercase tracking-widest rounded-xl hover:bg-gray-200 transition-colors shadow-[0_0_20px_rgba(255,255,255,0.2)] flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                  {isLoading ? 'INITIATING...' : (
                     <>
                        Slay the {featureType === 'chat' ? 'Chat' : 'Call'} <Zap size={20} className="fill-current text-black" strokeWidth={0} />
                     </>
                  )}
              </button>
          </div>
      </div>
    </main>
  );
};

export default ModeSelectionPage;
